
import { Component, Vue } from 'vue-property-decorator'
import {
  listWarrantyPeriods,
  createWarrantyPeriod,
  updateWarrantyPeriod,
  deleteWarrantyPeriod,
  WarrantyPeriod
} from '@/api/warrantyPeriods'

@Component({
  name: 'WarrantyPeriods'
})
export default class extends Vue {
  private loading = false
  private saving = false
  private showInactive = false
  private warrantyPeriods: WarrantyPeriod[] = []

  private dialogVisible = false
  private dialogType: 'create' | 'edit' = 'create'

  private formData = {
    id: 0,
    code: '',
    name: '',
    durationMonths: 12,
    sortOrder: 0,
    isActive: true
  }

  private formRules = {
    code: [
      { required: true, message: 'Code is required', trigger: 'blur' },
      { pattern: /^[a-z0-9_]+$/, message: 'Only lowercase letters, numbers and underscores', trigger: 'blur' }
    ],
    name: [
      { required: true, message: 'Name is required', trigger: 'blur' }
    ],
    durationMonths: [
      { required: true, message: 'Duration is required', trigger: 'blur' }
    ]
  }

  created() {
    this.loadData()
  }

  private async loadData() {
    this.loading = true
    try {
      const { data } = await listWarrantyPeriods(this.showInactive)
      this.warrantyPeriods = data.data || []
    } catch (error) {
      console.error('Failed to load warranty periods:', error)
      this.$message.error('Failed to load warranty periods')
    } finally {
      this.loading = false
    }
  }

  private handleCreate() {
    this.dialogType = 'create'
    this.formData = {
      id: 0,
      code: '',
      name: '',
      durationMonths: 12,
      sortOrder: this.warrantyPeriods.length + 1,
      isActive: true
    }
    this.dialogVisible = true
  }

  private handleEdit(row: WarrantyPeriod) {
    this.dialogType = 'edit'
    this.formData = {
      id: row.id,
      code: row.code,
      name: row.name,
      durationMonths: row.durationMonths,
      sortOrder: row.sortOrder,
      isActive: row.isActive
    }
    this.dialogVisible = true
  }

  private async handleSave() {
    const form = this.$refs.formRef as any
    const valid = await form.validate().catch(() => false)
    if (!valid) return

    this.saving = true
    try {
      if (this.dialogType === 'create') {
        await createWarrantyPeriod({
          code: this.formData.code,
          name: this.formData.name,
          durationMonths: this.formData.durationMonths,
          sortOrder: this.formData.sortOrder,
          isActive: this.formData.isActive
        })
        this.$message.success('Warranty period created successfully')
      } else {
        await updateWarrantyPeriod(this.formData.id, {
          name: this.formData.name,
          durationMonths: this.formData.durationMonths,
          sortOrder: this.formData.sortOrder,
          isActive: this.formData.isActive
        })
        this.$message.success('Warranty period updated successfully')
      }
      this.dialogVisible = false
      this.loadData()
    } catch (error) {
      console.error('Failed to save warranty period:', error)
      this.$message.error('Failed to save warranty period')
    } finally {
      this.saving = false
    }
  }

  private async handleDelete(row: WarrantyPeriod) {
    try {
      await this.$confirm(
        `Are you sure you want to delete "${row.name}"?`,
        'Confirm Delete',
        {
          confirmButtonText: 'Delete',
          cancelButtonText: 'Cancel',
          type: 'warning'
        }
      )

      await deleteWarrantyPeriod(row.id)
      this.$message.success('Warranty period deleted successfully')
      this.loadData()
    } catch (error: any) {
      if (error !== 'cancel') {
        console.error('Failed to delete warranty period:', error)
        this.$message.error('Failed to delete warranty period')
      }
    }
  }
}
